import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import ChatAgent from "@/components/ChatAgent";
import { 
  Building2, 
  FileText, 
  ArrowRight,
  CheckCircle,
  Trophy,
  Calendar,
  Users,
  Briefcase
} from "lucide-react";

const ApplicationSelect = () => {
  const companySetupFeatures = [
    "Trade License Registration",
    "Business Formation & Setup", 
    "VAT Registration",
    "Bank Account Opening",
    "Office Space Solutions",
    "Complete Documentation"
  ];

  const otherServicesFeatures = [
    "Event Management & Permits",
    "Event Ticketing & Accreditation",
    "Security & Cleaning Planning",
    "Sports Management Consultancy",
    "UAE Sports Council NOC Support",
    "Sports Federation NOC Assistance",
    "HR & Compliance Services (UAE-compliant)",
    "Organization Structure & Job Grading",
    "HR Policies & Appraisal Systems",
    "HR Forms & Templates", 
    "Corporate Training",
    "Legal Advisory",
    "Custom Business Solutions"
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3">
              <img 
                src="./images/logo.jpg" 
                alt="Company Logo" 
                className="h-12 w-auto object-contain"
              />
              <span className="text-3xl font-bold text-primary">Elite Edge Management Consultancy</span>
            </Link>
            <div className="hidden md:flex items-center space-x-6">
              <Link to="/" className="text-foreground hover:text-primary transition-colors">Home</Link>
              <Link to="/services" className="text-foreground hover:text-primary transition-colors">Services</Link>
              <Link to="/login" className="text-foreground hover:text-primary transition-colors">Admin</Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Header */}
      <section className="py-16 px-4 bg-gradient-to-br from-primary/5 via-primary/10 to-accent/5">
        <div className="container mx-auto text-center">
          <Badge className="mb-4" variant="secondary">Choose Your Application Type</Badge>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Submit Your Application</h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Select the type of service you need to get started with the right application form
          </p>
        </div>
      </section>

      {/* Application Options */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-8">
            
            {/* Company Setup Application */}
            <Card className="group hover:shadow-2xl transition-all duration-300 border-2 hover:border-primary/50">
              <CardHeader className="text-center pb-6">
                <div className="w-20 h-20 bg-gradient-to-r from-primary to-primary-glow rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                  <Building2 className="h-10 w-10 text-white" />
                </div>
                <CardTitle className="text-2xl mb-2">Company Setup Application</CardTitle>
                <CardDescription className="text-base">
                  Complete application for business formation, trade license registration, and company setup services
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h4 className="font-semibold mb-3 text-lg">What's Included:</h4>
                  <div className="space-y-2">
                    {companySetupFeatures.map((feature, index) => (
                      <div key={index} className="flex items-center">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-3 flex-shrink-0" />
                        <span className="text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h5 className="font-semibold text-blue-900 mb-2">Perfect For:</h5>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• New business owners</li>
                    <li>• Trade license applications</li>
                    <li>• Company formation & setup</li>
                    <li>• VAT registration needs</li>
                  </ul>
                </div>

                <Button asChild className="w-full bg-gradient-to-r from-primary to-primary-glow text-lg py-6">
                  <Link to="/client-form">
                    Start Company Setup Application
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
              </CardContent>
            </Card>

            {/* Other Services Request */}
            <Card className="group hover:shadow-2xl transition-all duration-300 border-2 hover:border-accent/50">
              <CardHeader className="text-center pb-6">
                <div className="w-20 h-20 bg-gradient-to-r from-accent to-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                  <Briefcase className="h-10 w-10 text-white" />
                </div>
                <CardTitle className="text-2xl mb-2">Other Service Request</CardTitle>
                <CardDescription className="text-base">
                  Request form for specialized services like event management, sports consultancy, HR services, and more
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h4 className="font-semibold mb-3 text-lg">Services Available:</h4>
                  <div className="space-y-2">
                    {otherServicesFeatures.map((feature, index) => (
                      <div key={index} className="flex items-center">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-3 flex-shrink-0" />
                        <span className="text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="bg-orange-50 p-4 rounded-lg">
                  <h5 className="font-semibold text-orange-900 mb-2">Perfect For:</h5>
                  <ul className="text-sm text-orange-800 space-y-1">
                    <li>• Event planning & permits</li>
                    <li>• Ticketing & accreditation systems</li>
                    <li>• Security & operational planning</li>
                    <li>• Sports management & NOC support</li>
                    <li>• UAE Sports Council assistance</li>
                    <li>• UAE-compliant HR systems</li>
                    <li>• Organization structure & policies</li>
                    <li>• Custom business solutions</li>
                  </ul>
                </div>

                <Button asChild className="w-full bg-gradient-to-r from-accent to-orange-500 text-lg py-6">
                  <Link to="/service-request">
                    Submit Service Request
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Help Section */}
          <div className="mt-12 text-center">
            <Card className="max-w-2xl mx-auto">
              <CardContent className="pt-6">
                <h3 className="text-xl font-semibold mb-4">Need Help Choosing?</h3>
                <p className="text-muted-foreground mb-6">
                  Not sure which application form is right for you? Our AI assistant can help guide you to the right option, 
                  or you can contact us directly for personalized assistance.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button variant="outline" asChild>
                    <Link to="/services">View All Services</Link>
                  </Button>
                  <Button variant="outline">
                    <Link to="mailto:info@eliteedgemc.ae">Contact Us Directly</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Chat Agent */}
      <ChatAgent />
    </div>
  );
};

export default ApplicationSelect;